import { Button } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router';
import IMAGES from '../../assets/images';
import CustomImage from '../../components/CustomImage';
import LoginModal from '../../components/LoginModal';
import useAuth from '../../hooks/useAuth';
import useDiscloser from '../../hooks/useDiscloser';
import useSectionNav from '../../hooks/useSectionNav';
import { Menu } from '../../icons';
import { CONTACT_PATH } from '../../constants/company.constants';
import cn from '../../lib/cn';
import ProfileMenu from './ProfileMenu';
import { PATHS } from '../../routes/paths';

const JOB_PORTAL_PATH = PATHS.jobPortal.root;
const NAVBAR_HEIGHT = 64;

// `section` items scroll to a home page section, `path` items open a page.
type NavItem = { key: string; label: string; section?: string; path?: string };

const NAV_ITEMS: NavItem[] = [
  { key: 'features', label: 'Features', section: 'features' },
  { key: 'pricing', label: 'Pricing', section: 'pricing' },
  { key: 'job-portal', label: 'Job Portal', path: JOB_PORTAL_PATH },
];

const SECTION_IDS = NAV_ITEMS.flatMap((item) =>
  item.section ? [item.section] : [],
);

/** The navbar section under the navbar, or '' anywhere else on the page. */
const useActiveSection = (enabled: boolean) => {
  const [active, setActive] = useState('');

  useEffect(() => {
    if (!enabled) {
      setActive('');
      return;
    }
    const compute = () => {
      const probe = NAVBAR_HEIGHT + 56;
      const current = SECTION_IDS.find((id) => {
        const rect = document.getElementById(id)?.getBoundingClientRect();
        return rect && rect.top <= probe && rect.bottom > probe;
      });
      setActive(current ?? '');
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    return () => window.removeEventListener('scroll', compute);
  }, [enabled]);

  return active;
};

const WebNavbar = () => {
  const { verifiedInfo } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goToSection = useSectionNav();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Every page mounts its own navbar. A per-mount layoutId keeps the active
  // bar sliding between items on a page, without framer-motion animating it
  // in from the previous page's (scrolled) position.
  const [barLayoutId] = useState(
    () => `nav-active-bar-${Math.random().toString(36).slice(2)}`,
  );
  const [config, handleOpen, handleClose] = useDiscloser<'LOGIN'>();

  const isHome = pathname === '/';
  const activeSection = useActiveSection(isHome);
  const isJobPortal =
    pathname === JOB_PORTAL_PATH || pathname.startsWith(`${JOB_PORTAL_PATH}/`);
  const activeKey = isJobPortal ? 'job-portal' : activeSection;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (item: NavItem) => {
    setMobileMenuOpen(false);
    if (item.section) {
      goToSection(item.section);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleLogoClick = () => {
    setMobileMenuOpen(false);
    if (isHome) window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300',
        scrolled || mobileMenuOpen
          ? 'border-gray-200/80 bg-white/85'
          : 'border-transparent bg-white/60',
      )}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          aria-label="HRx home"
          className="shrink-0"
        >
          <CustomImage
            src={IMAGES.hrxLogo}
            width={76}
            height={40}
            alt="HRX Logo"
          />
        </button>

        {/* Desktop */}
        <div className="hidden h-full items-center lg:flex">
          <div className="flex h-full items-center">
            {NAV_ITEMS.map((item) => {
              const isActive = activeKey === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  aria-current={
                    isActive ? (item.path ? 'page' : 'true') : undefined
                  }
                  className={cn(
                    'relative flex h-full items-center px-3.5 text-[15px] font-medium transition-colors',
                    isActive
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-900',
                  )}
                >
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId={barLayoutId}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 40,
                      }}
                      className="absolute inset-x-3.5 -bottom-px h-0.5 rounded-full bg-theme-primary"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <span aria-hidden className="mx-4 h-5 w-px bg-gray-200" />

          {!verifiedInfo ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpen({ type: 'LOGIN' })}
                className="px-3 py-1.5 text-[15px] font-medium text-gray-700 transition-colors hover:text-gray-900"
              >
                Log in
              </button>
              <Link to={CONTACT_PATH} onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-primary h-9 rounded-full px-4 font-medium">
                  Request a Demo
                </Button>
              </Link>
            </div>
          ) : (
            <ProfileMenu />
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
          className="-mr-2 rounded-lg p-2 text-gray-700 lg:hidden"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gray-100 bg-white lg:hidden"
          >
            <div className="px-4 py-4 sm:px-6">
              <div className="space-y-1">
                {NAV_ITEMS.map((item) => {
                  const isActive = activeKey === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleNavClick(item)}
                      className={cn(
                        'relative block w-full py-2.5 pl-4 pr-3 text-left text-base font-medium transition-colors',
                        isActive
                          ? 'text-gray-900'
                          : 'text-gray-500 hover:text-gray-900',
                      )}
                    >
                      {isActive && (
                        <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-theme-primary" />
                      )}
                      {item.label}
                    </button>
                  );
                })}
              </div>

              {!verifiedInfo ? (
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-4">
                  <Button
                    size="large"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleOpen({ type: 'LOGIN' });
                    }}
                    className="font-medium"
                  >
                    Log in
                  </Button>
                  <Link
                    to={CONTACT_PATH}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      size="large"
                      className="btn-primary w-full font-medium"
                    >
                      Request a Demo
                    </Button>
                  </Link>
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/*  Modal */}
      <LoginModal
        data={config.data}
        type={config.type}
        handleClose={handleClose}
        open={config.open && config.type === 'LOGIN'}
      />
    </header>
  );
};

export default WebNavbar;
